import { Flex, Skeleton } from 'antd';
import { AccessDenied } from 'components/AccessDenied';
import { BaseHeader } from 'components/header';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { PrintableRootPage } from 'components/page/page-2';
import { Role } from 'features/auth/types';
import { ConcussionRXLogo } from 'features/sign-in/v2/concussionRx-logo';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { useClinicAdminData } from 'lib/hooks/tanstack/useClinicAdminData';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useEffect, useState } from 'react';
import { useErrorList } from 'lib/utils/useErrorList';
import BillingPage from './billing_page';
import OrganisationRegistrationFormPage from './organisation_registration_form_page';
import RegistrationSteps from './RegistrationSteps';
import { BaseButton } from 'components/button/button-base';

export default function AuthenticatedClinicAdminFlow() {
  const auth = useAuth();
  const [currentStep, setCurrentStep] = useState(1); // Starts at step 1 for authenticated users
  const errorList = useErrorList();
  const [needsOnboardingError, setNeedsOnboardingError] = useState(false);

  const clinicAdminDataQuery = useClinicAdminData({
    uid: auth.user?.uid,
    enabled: auth.user?.role === Role.ClinicAdmin // Only enable if user is ClinicAdmin
  });

  useQueryErrorNotifier(clinicAdminDataQuery, errorList, 'Clinic admin data');

  useEffect(() => {
    if (!clinicAdminDataQuery.isLoading && clinicAdminDataQuery.data) {
      if (clinicAdminDataQuery.data.data.needsOnboarding === false) {
        setNeedsOnboardingError(true);
        errorList.addError(
          new Error(
            'You have already completed organiastion creation. Please contact support if you believe this is an error.'
          ),
          'onboarding-already-complete'
        );
      }
    }
  },
  // eslint-disable-next-line
  [clinicAdminDataQuery.data, clinicAdminDataQuery.isLoading, errorList.addError]);

  // Loading state
  if (auth.isUserDataLoading || clinicAdminDataQuery.isLoading) {
    return <Skeleton active />;
  }

  // Access control: Must be ClinicAdmin
  if (auth.user?.role !== Role.ClinicAdmin) {
    return <AccessDenied />;
  }

  // Error display for already onboarded or other errors
  if (errorList.hasErrors || needsOnboardingError) {
    return (
      <PrintableRootPage>
        <BaseHeader
          headerStyle={{
            background: 'none'
          }}
        />
        <Flex align="center" justify="center">
          <ConcussionRXLogo
            style={{ width: 'revert', height: '50px', marginBottom: '20px' }}
          />
        </Flex>
        <Flex vertical gap={16}>
          <ErrorListDisplay errorList={errorList} />

          <div>
            
            <BaseButton style={{ display: 'flex', width: 'min-content'}} href={'/'}>Navigate to the home page</BaseButton>
          </div>
        </Flex>
      </PrintableRootPage>
    );
  }

  function goToNextStep() {
    setCurrentStep(prevStep => prevStep + 1);
  }

  function renderStep() {
    switch (currentStep) {
      case 1:
        return <OrganisationRegistrationFormPage goToNextStep={goToNextStep} />;
      case 2:
        return <BillingPage />;
      default:
        return <div>Unknown Step</div>;
    }
  }

  return (
    <PrintableRootPage>
      <BaseHeader
        headerStyle={{
          background: 'none'
        }}
      />

      <Flex align="center" justify="center">
        <ConcussionRXLogo
          style={{ width: 'revert', height: '50px', marginBottom: '20px' }}
        />
      </Flex>
      <RegistrationSteps current={currentStep} />
      {renderStep()}
    </PrintableRootPage>
  );
}
